import React from "react";
import { useGameReach } from "../hooks/useGameReach";
import ReachStatus from "./ReachStatus";
import PushOption, { localPermission } from "./PushOption";
import TelegramOption from "./TelegramOption";

interface Props {
  /** "Next" in the join flow, "Done" when fixing reach for a player already in. */
  nextLabel: string;
  onNext: () => void;
  onBack?: () => void;
  /** The server's message when a join was refused as not reachable. */
  serverMessage?: string | null;
}

/**
 * Step 2 (required): make sure the game can reach this person. Either the site
 * as an app with notifications on, or Telegram linked. Next stays disabled
 * until GET /game/reach says reachable; the status polls while the step is
 * open and re-checks on return to the tab.
 */
const StepReach: React.FC<Props> = ({ nextLabel, onNext, onBack, serverMessage }) => {
  const { reach, errorMessage, checking, recheck, watch } = useGameReach(true);
  const reachable = reach?.reachable === true;

  return (
    <div className="gm-step">
      <h2 className="gm-step-title">Make sure the game can reach you.</h2>
      <p>
        The race is every morning and the game lives on it. To play, turn on one of these: the
        site as an app with notifications on, or Telegram.
      </p>
      {serverMessage && !reachable ? <p className="gm-error">{serverMessage}</p> : null}
      <ReachStatus
        reach={reach}
        errorMessage={errorMessage}
        checking={checking}
        localPermission={localPermission()}
        onRecheck={recheck}
      />
      <PushOption reach={reach} onChanged={recheck} />
      <div className="gm-option__or" aria-hidden>
        or
      </div>
      <TelegramOption state={reach?.telegram.state} onOpened={watch} />
      <div className="gm-nav">
        <div className="gm-nav__row">
          {onBack ? (
            <button type="button" className="gm-button gm-button--plain" onClick={onBack}>
              Back
            </button>
          ) : null}
          <button
            type="button"
            className="gm-button"
            onClick={onNext}
            disabled={!reachable}
          >
            {nextLabel}
          </button>
        </div>
        {!reachable ? (
          <p className="gm-nav__hint">{nextLabel} unlocks when one of the two is on.</p>
        ) : null}
      </div>
    </div>
  );
};

export default StepReach;
